import type { RootState } from '@/store';
import { createAsyncThunk } from '@reduxjs/toolkit';

import type {
  CancelNegociacaoStartResult,
  ClosePhoneConflictResult,
  CpfCnpjValidationData,
  CreateNegociacaoIndividualInput,
  CreateNegociacaoIndividualResult,
  Flow,
  ValidatePhoneConflictInput,
  ValidatePhoneConflictResult,
} from '../types';
import type {
  PublishedAgentCatalogPage,
  PublishedAgentPageQuery,
} from '../types/publishedAgentCatalog';
import { negociacaoIndividualWizardService } from './services';

export const fetchNegociacaoIndividualAgents = createAsyncThunk<
  PublishedAgentCatalogPage,
  PublishedAgentPageQuery,
  { rejectValue: string; state: RootState }
>('negociacaoIndividualWizard/fetchAgents', async (query, { rejectWithValue, getState }) => {
  try {
    const state = getState();
    const user = state.auth.user;

    if (!user?.empresaId) {
      return rejectWithValue('Não autenticado');
    }

    return await negociacaoIndividualWizardService.listAgents(query);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao carregar agentes');
  }
});

export const fetchNegociacaoIndividualAgent = createAsyncThunk<
  Flow,
  string,
  { rejectValue: string; state: RootState }
>('negociacaoIndividualWizard/fetchAgent', async (flowId, { rejectWithValue, getState }) => {
  try {
    const state = getState();
    const user = state.auth.user;

    if (!user?.empresaId) {
      return rejectWithValue('Não autenticado');
    }

    return await negociacaoIndividualWizardService.getAgentById(flowId);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao carregar detalhes do agente',
    );
  }
});

export const validarCpfCnpjWizard = createAsyncThunk<
  CpfCnpjValidationData,
  string,
  { rejectValue: string; state: RootState }
>('negociacaoIndividualWizard/validarCpfCnpj', async (doc, { rejectWithValue, getState }) => {
  try {
    const state = getState();
    const user = state.auth.user;

    if (!user?.empresaId) {
      return rejectWithValue('Não autenticado');
    }

    return await negociacaoIndividualWizardService.validarCpfCnpj(doc);
  } catch (error) {
    console.log({ error });
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao validar CPF/CNPJ');
  }
});

export const createNegociacaoIndividual = createAsyncThunk<
  CreateNegociacaoIndividualResult,
  CreateNegociacaoIndividualInput,
  { rejectValue: string; state: RootState }
>('negociacaoIndividualWizard/createNegociacaoIndividual', async (input, { rejectWithValue }) => {
  try {
    return await negociacaoIndividualWizardService.createNegociacaoIndividual(input);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao criar negociação');
  }
});

export const cancelNegociacaoIndividualStart = createAsyncThunk<
  CancelNegociacaoStartResult,
  string,
  { rejectValue: string; state: RootState }
>('negociacaoIndividualWizard/cancelStart', async (caseId, { rejectWithValue }) => {
  try {
    return await negociacaoIndividualWizardService.cancelStart(caseId);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao cancelar início');
  }
});

export const validatePhoneConflictWizard = createAsyncThunk<
  ValidatePhoneConflictResult,
  ValidatePhoneConflictInput,
  { rejectValue: string; state: RootState }
>('negociacaoIndividualWizard/validatePhoneConflict', async (input, { rejectWithValue }) => {
  try {
    return await negociacaoIndividualWizardService.validatePhoneConflict(input);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao validar conflito por WhatsApp',
    );
  }
});

export const closePhoneConflictWizard = createAsyncThunk<
  ClosePhoneConflictResult,
  string,
  { rejectValue: string; state: RootState }
>('negociacaoIndividualWizard/closePhoneConflict', async (caseId, { rejectWithValue }) => {
  try {
    return await negociacaoIndividualWizardService.closePhoneConflict(caseId);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao encerrar negociação em conflito',
    );
  }
});
